import React, { PropsWithChildren } from "react";

import { FieldContainer, InputMessage } from "emoreg";
import { Flex, FlexProps } from "emoreg/layouts";
import { isTruthy } from "hassibot/util/guards";

import { GroupedRadioContext } from "../context";

export type GroupedRadioContainerProps<T extends string> = PropsWithChildren<{
  name: string;
  value?: T | null;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onChangeValue?: (value: T) => void;
  direction?: FlexProps["direction"];
  align?: FlexProps["alignItems"];
  hasError?: boolean;
  error?: string;
  style?: React.CSSProperties;
  className?: string;
}>;

export const Container = <T extends string>({
  style,
  className,
  name,
  value,
  onChange,
  onChangeValue,
  hasError,
  align = "flex-start",
  direction = "row",
  error,
  children,
}: GroupedRadioContainerProps<T>) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e);
    onChangeValue?.(e.target.value as T);
  };

  return (
    <GroupedRadioContext.Provider
      value={{
        name,
        value,
        hasError: hasError || !!error,
        onChange: handleChange,
        "aria-labelledby": [`label_${name}`, !!error && `${error}_${name}`]
          .filter(isTruthy)
          .join(" "),
      }}
    >
      <FieldContainer style={style} className={className}>
        {/* this layer could be redefined every time by user.
          by keeping it here, we ensure consistency accross all gaps */}
        <Flex wrap="wrap" direction={direction} gap={8} alignItems={align}>
          {children}
        </Flex>
        {error && (
          <InputMessage variant="error" id={`${error}_$name`}>
            {error}
          </InputMessage>
        )}
      </FieldContainer>
    </GroupedRadioContext.Provider>
  );
};
